import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Clock, SignIn, SignOut, X, Backspace, ShieldCheck, Eye, EyeSlash, Camera, WarningCircle, ChartLineUp, Receipt, Coffee } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import IncidenceDialog from "../components/IncidenceDialog";
import WorkerHistoryDialog from "../components/WorkerHistoryDialog";
import BreakDialog from "../components/BreakDialog";
import PinPad from "../components/PinPad";
import { useRoles, roleLabel } from "../lib/roles";

const CLOCK_TICK_MS = 1000;
const WORKERS_REFRESH_MS = 30000;
const MAX_PIN_LENGTH = 20;

function useClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const tick = setInterval(() => setNow(new Date()), CLOCK_TICK_MS);
    return () => clearInterval(tick);
  }, []);
  return now;
}

function formatTime(d) {
  return d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}
function formatDate(d) {
  return d.toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

export default function Fichaje() {
  const now = useClock();
  const [roles] = useRoles();
  const navigate = useNavigate();
  const [accessGranted, setAccessGranted] = useState(false);
  const [accessRequired, setAccessRequired] = useState(false);
  const [accessPin, setAccessPin] = useState("");
  const [accessChecking, setAccessChecking] = useState(false);
  const [workers, setWorkers] = useState([]);
  const [statuses, setStatuses] = useState({}); // userId -> isClockedIn
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [pin, setPin] = useState("");
  const [showPin, setShowPin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [geofenceEnabled, setGeofenceEnabled] = useState(false);
  const [uploadPinEnabled, setUploadPinEnabled] = useState(false);
  const [uploadPin, setUploadPin] = useState("");
  const [uploadPinModal, setUploadPinModal] = useState(false);
  const [uploadingInvoice, setUploadingInvoice] = useState(false);
  const [invoiceResult, setInvoiceResult] = useState(null);
  const [incidenceWorker, setIncidenceWorker] = useState(null);
  const [incidencePin, setIncidencePin] = useState("");
  // PIN gate for opening incidence dialog or history view (auth before UI)
  const [gateWorker, setGateWorker] = useState(null);          // worker being verified
  const [gateAction, setGateAction] = useState(null);          // 'incidence' | 'history'
  const [gatePin, setGatePin] = useState("");
  const [gateError, setGateError] = useState("");
  const [gateChecking, setGateChecking] = useState(false);
  const [historyWorker, setHistoryWorker] = useState(null);    // worker viewing their hours
  const [breakWorker, setBreakWorker] = useState(null);        // worker toggling break
  const [breakPin, setBreakPin] = useState("");                // PIN remembered right after clock-in
  const uploadBypassInputRef = useRef(null);
  const bypassUploadPinRef = useRef(false);

  const openGate = (worker, action) => {
    setGateWorker(worker);
    setGateAction(action);
    setGatePin("");
    setGateError("");
  };
  const closeGate = () => {
    setGateWorker(null);
    setGateAction(null);
    setGatePin("");
    setGateError("");
    setGateChecking(false);
  };
  const submitGate = async () => {
    if (!gatePin) {
      setGateError("Introduce la contraseña");
      return;
    }
    setGateChecking(true);
    setGateError("");
    try {
      // Verify PIN via a lightweight endpoint. We reuse punch verification: /workers/{id}/verify-pin
      const r = await api.post(`/workers/${gateWorker.id}/verify-pin`, { pin: gatePin });
      if (!r.data?.ok) throw new Error("Contraseña incorrecta");
      const w = gateWorker;
      const act = gateAction;
      const pin = gatePin;
      closeGate();
      if (act === "incidence") {
        setIncidencePin(pin);
        setIncidenceWorker(w);
      } else if (act === "history") {
        setHistoryWorker({ ...w, __pin: pin });
      }
    } catch (e) {
      setGateError(e.response?.data?.detail || e.message || "Contraseña incorrecta");
    } finally {
      setGateChecking(false);
    }
  };

  const fetchWorkers = async () => {
    try {
      const { data } = await api.get("/workers");
      const sorted = [...data].sort((a, b) =>
        (a?.name || "").localeCompare(b?.name || "", "es", { sensitivity: "base" })
      );
      setWorkers(sorted);
      const map = {};
      await Promise.all(
        sorted.map(async (w) => {
          try {
            const r = await api.get(`/workers/${w.id}/status`);
            map[w.id] = { is_clocked_in: r.data.is_clocked_in, on_break: r.data.on_break };
          } catch (err) {
            map[w.id] = { is_clocked_in: false, on_break: false };
          }
        })
      );
      setStatuses(map);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Always require the access PIN on entry — do not remember across navigations.
    api
      .post("/settings/verify-access-pin", { pin: "" })
      .then((r) => {
        const enabled = !!r.data.enabled;
        setAccessRequired(enabled);
        setAccessGranted(!enabled);
      })
      .catch(() => {
        setAccessRequired(false);
        setAccessGranted(true);
      });
    fetchWorkers();
    api.get("/settings/public").then((r) => setGeofenceEnabled(!!r.data.geofence_enabled)).catch(() => {});
    api.get("/settings/public/upload-enabled").then((r) => setUploadPinEnabled(!!r.data.enabled)).catch(() => {});
    const refresh = setInterval(fetchWorkers, WORKERS_REFRESH_MS);
    return () => clearInterval(refresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitAccessPin = async (e) => {
    e?.preventDefault?.();
    if (!accessPin.trim()) return;
    setAccessChecking(true);
    try {
      const r = await api.post("/settings/verify-access-pin", { pin: accessPin });
      if (r.data.ok) {
        // Remember the gate pin for the current browser tab so that opening the
        // invoice uploader or /pedidos from here does not ask for it again.
        try { sessionStorage.setItem("fichaje_access_pin", accessPin); } catch (err) { console.warn("sessionStorage unavailable", err); }
        setAccessGranted(true);
        setAccessPin("");
      } else {
        toast.error("PIN incorrecto");
        setAccessPin("");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAccessChecking(false);
    }
  };

  const openPin = (worker) => {
    setSelectedWorker(worker);
    setPin("");
    setShowPin(false);
  };

  const closePin = () => {
    setSelectedWorker(null);
    setPin("");
  };

  const pressKey = (k) => {
    if (pin.length < MAX_PIN_LENGTH) setPin((p) => p + k);
  };
  const backspace = () => setPin((p) => p.slice(0, -1));

  const getLocation = () =>
    new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Tu dispositivo no soporta geolocalización"));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        (err) => reject(new Error("Permite la ubicación para fichar: " + err.message)),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
      );
    });

  const submitInvoiceUpload = async (file) => {
    if (!file) return;
    setUploadingInvoice(true);
    setInvoiceResult(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(
        `/invoices/analyze/public?pin=${encodeURIComponent(uploadPin)}`,
        fd,
        {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 180000, // Gemini can take up to ~3 min with big PDFs
        }
      );
      setInvoiceResult(data);
      toast.success(
        data.supplier_created
          ? `Proveedor "${data.supplier.name}" creado y factura guardada`
          : `Factura guardada en "${data.supplier.name}"`
      );
      setUploadPinModal(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploadingInvoice(false);
    }
  };

  const [breakEndPrompt, setBreakEndPrompt] = useState(null); // {open_break, savedPayload}

  const submit = async (extra = {}) => {
    if (!selectedWorker || !pin) return;
    setLoading(true);
    // Guard against callers that accidentally pass a React/DOM event
    // (e.g. onClick={submit} spreading a synthetic event into the payload).
    const safeExtra = extra && typeof extra === "object" && !extra.nativeEvent && !extra.preventDefault
      ? extra
      : {};
    const payload = { user_id: selectedWorker.id, password: pin, ...safeExtra };
    try {
      // Reuse coordinates saved from the previous attempt (e.g. when we come back
      // from the OPEN_BREAK 409 prompt). We do this BEFORE the geofence branch so
      // the retry does not need to ask for location again.
      if (breakEndPrompt?.savedPayload) {
        if (payload.lat == null && breakEndPrompt.savedPayload.lat != null) {
          payload.lat = breakEndPrompt.savedPayload.lat;
        }
        if (payload.lng == null && breakEndPrompt.savedPayload.lng != null) {
          payload.lng = breakEndPrompt.savedPayload.lng;
        }
      }
      if (geofenceEnabled && (payload.lat == null || payload.lng == null)) {
        try {
          const coords = await getLocation();
          payload.lat = coords.lat;
          payload.lng = coords.lng;
        } catch (geoErr) {
          toast.error(geoErr.message);
          setLoading(false);
          return;
        }
      }
      const { data } = await api.post("/punch/toggle", payload);
      const clockedWorker = selectedWorker;
      const usedPin = pin;
      if (data.action === "entrada") toast.success(`¡Bienvenido, ${data.name}! Fichaje de entrada registrado.`);
      else toast.success(`Hasta luego, ${data.name}. Fichaje de salida registrado.`);
      setBreakEndPrompt(null);
      closePin();
      fetchWorkers();
      if (data.action === "entrada") {
        setBreakPin(usedPin);
        setBreakWorker(clockedWorker);
      }
    } catch (e) {
      // Handle open-break 409 → show a small prompt to enter the break end time
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.code === "OPEN_BREAK") {
        const nowLocal = new Date();
        const hh = String(nowLocal.getHours()).padStart(2, "0");
        const mm = String(nowLocal.getMinutes()).padStart(2, "0");
        setBreakEndPrompt({
          open_break: detail.open_break,
          savedPayload: { lat: payload.lat, lng: payload.lng },
          break_end_time: `${hh}:${mm}`,
        });
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: "var(--bg)" }}>
      {/* PIN gate — required to enter the Fichaje screen */}
      {accessRequired && !accessGranted && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-md z-[60] flex items-center justify-center p-4">
          <form
            onSubmit={submitAccessPin}
            className="card-flat w-full max-w-sm p-8 relative"
            data-testid="access-pin-modal"
          >
            <button
              type="button"
              onClick={() => navigate("/")}
              className="absolute top-4 right-4 w-8 h-8 rounded-full hover:bg-[var(--border)] flex items-center justify-center"
              data-testid="access-pin-close"
            >
              <X size={18} />
            </button>
            <div className="flex items-center gap-3 mb-5">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ background: "var(--primary)" }}
              >
                <ShieldCheck size={24} color="#FFF" weight="duotone" />
              </div>
              <div>
                <div className="font-display text-xl font-bold tracking-tight">Acceso al fichaje</div>
                <div className="label-cap">introduce el PIN</div>
              </div>
            </div>
            <PinPad
              value={accessPin}
              onChange={setAccessPin}
              maxLength={8}
              testIdPrefix="access-pinpad"
              inputTestId="access-pin-input"
              placeholder="••••"
            />
            <button
              type="submit"
              disabled={accessChecking || !accessPin}
              className="btn-primary w-full mt-4 px-4 py-3 rounded-xl font-display font-bold disabled:opacity-50"
              data-testid="access-pin-submit"
            >
              {accessChecking ? "Comprobando…" : "Entrar"}
            </button>
          </form>
        </div>
      )}
      {/* subtle texture overlay */}
      <div
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 10% 20%, rgba(74,93,35,0.06) 0%, transparent 40%), radial-gradient(circle at 90% 80%, rgba(224,122,95,0.06) 0%, transparent 40%)",
        }}
      />
      <div className="relative max-w-6xl mx-auto px-6 py-8 md:py-12">
        <header className="flex items-center justify-between mb-10">
          <Link to="/" className="flex items-center gap-3 group" data-testid="fichaje-home-link">
            <img
              src={process.env.REACT_APP_LOGO_URL}
              alt="Logo"
              className="w-12 h-12 rounded-xl object-cover border border-[var(--border)] group-hover:scale-105 transition"
            />
            <div>
              <div className="font-display text-xl font-bold tracking-tight">Fichaje</div>
              <div className="label-cap">control horario</div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            {uploadPinEnabled && (
              <button
                onClick={() => {
                  // If the gate PIN already unlocked /fichaje, reuse it for the
                  // uploader (both share invoice_upload_pin) and skip the modal.
                  const sessionPin = (() => { try { return sessionStorage.getItem("fichaje_access_pin") || ""; } catch (_) { return ""; } })();
                  if (sessionPin) {
                    setUploadPin(sessionPin);
                    bypassUploadPinRef.current = true;
                    uploadBypassInputRef.current?.click();
                  } else {
                    setUploadPin("");
                    bypassUploadPinRef.current = false;
                    setUploadPinModal(true);
                  }
                }}
                data-testid="fichaje-upload-invoice-btn"
                className="flex items-center gap-2 px-4 py-2 rounded-full border border-[var(--border)] hover:bg-white transition text-sm font-display font-medium"
              >
                <Camera size={16} weight="duotone" />
                Cargar factura
              </button>
            )}
            {/* Hidden file input used when the gate already provided the PIN */}
            <input
              ref={uploadBypassInputRef}
              type="file"
              accept="image/*,application/pdf"
              capture="environment"
              className="hidden"
              data-testid="fichaje-upload-bypass-input"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) submitInvoiceUpload(file);
                e.target.value = "";
              }}
            />
            <Link
              to="/pedidos"
              data-testid="fichaje-orders-link"
              className="flex items-center gap-2 px-4 py-2 rounded-full border border-[var(--border)] hover:bg-white transition text-sm font-display font-medium"
            >
              <Receipt size={16} weight="duotone" />
              Pedidos
            </Link>
            <Link
              to="/admin/login"
              data-testid="admin-access-link"
              className="flex items-center gap-2 px-4 py-2 rounded-full border border-[var(--border)] hover:bg-white transition text-sm font-display font-medium"
            >
              <ShieldCheck size={16} weight="duotone" />
              Acceso admin
            </Link>
          </div>
        </header>

        <section className="text-center mb-12">
          <div className="label-cap mb-3" data-testid="current-date">{formatDate(now)}</div>
          <div
            className="font-display font-bold tracking-tighter leading-none mb-2"
            style={{ fontSize: "clamp(4rem, 14vw, 9rem)", color: "var(--text-primary)" }}
            data-testid="current-time"
          >
            {formatTime(now).split(":").map((p, i) => (
              <span key={["hours", "minutes", "seconds"][i]}>
                {i > 0 && <span className="blink">:</span>}
                {p}
              </span>
            ))}
          </div>
          <p className="text-[var(--text-secondary)] font-display">Selecciona tu nombre para fichar entrada o salida</p>
        </section>

        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {workers.length === 0 && (
            <div className="col-span-full card-flat p-12 text-center text-[var(--text-secondary)]">
              No hay trabajadores registrados. Inicia sesión como administrador para añadir personal.
            </div>
          )}
          {workers.map((w) => {
            const clockedIn = !!statuses[w.id]?.is_clocked_in;
            return (
              <div
                key={w.id}
                data-testid={`worker-card-${w.id}`}
                className="card-flat p-6 text-left hover:shadow-lg hover:-translate-y-1 transition-all group relative"
              >
                <div className="absolute top-4 right-4 flex flex-col gap-1.5">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openGate(w, "incidence");
                    }}
                    data-testid={`incidence-btn-${w.id}`}
                    className="w-8 h-8 rounded-full border border-[var(--border)] bg-white/70 flex items-center justify-center hover:bg-[var(--primary)] hover:text-white hover:border-[var(--primary)] transition"
                    title="Reportar incidencia"
                  >
                    <WarningCircle size={16} weight="duotone" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openGate(w, "history");
                    }}
                    data-testid={`history-btn-${w.id}`}
                    className="w-8 h-8 rounded-full border border-[var(--border)] bg-white/70 flex items-center justify-center hover:bg-[var(--primary)] hover:text-white hover:border-[var(--primary)] transition"
                    title="Ver mis horas trabajadas"
                  >
                    <ChartLineUp size={16} weight="duotone" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setBreakPin("");
                      setBreakWorker(w);
                    }}
                    data-testid={`break-btn-${w.id}`}
                    className={`w-8 h-8 rounded-full border flex items-center justify-center transition ${statuses[w.id]?.on_break ? "bg-amber-500 text-white border-amber-500" : "border-[var(--border)] bg-white/70 hover:bg-amber-500 hover:text-white hover:border-amber-500"}`}
                    title={statuses[w.id]?.on_break ? "Finalizar tarea" : "Iniciar tarea"}
                  >
                    <Coffee size={16} weight="duotone" />
                  </button>
                </div>
                <button
                  onClick={() => openPin(w)}
                  data-testid={`worker-fichar-${w.id}`}
                  className="w-full text-left"
                >
                  <div className="flex items-center justify-between mb-4 pr-10">
                    <div className="label-cap">{roleLabel(roles, w.role)}</div>
                    <span
                      className={`text-xs px-2 py-1 rounded-full font-display font-semibold ${
                        clockedIn
                          ? "bg-[#4A5D23] text-white"
                          : "bg-[var(--border)] text-[var(--text-secondary)]"
                      }`}
                    >
                      {clockedIn ? "Fichado" : "Fuera"}
                    </span>
                  </div>
                  <div className="font-display text-2xl font-bold tracking-tight mb-2">{w.name}</div>
                  <div className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
                    {clockedIn ? (
                      <>
                        <SignOut size={18} weight="duotone" color="#E07A5F" />
                        <span>Pulsa para fichar salida</span>
                      </>
                    ) : (
                      <>
                        <SignIn size={18} weight="duotone" color="#4A5D23" />
                        <span>Pulsa para fichar entrada</span>
                      </>
                    )}
                  </div>
                </button>
              </div>
            );
          })}
        </section>
      </div>

      {/* PIN modal */}
      {selectedWorker && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="card-flat w-full max-w-md p-8 relative">
            <button
              onClick={closePin}
              data-testid="pin-modal-close"
              className="absolute top-4 right-4 w-8 h-8 rounded-full hover:bg-[var(--border)] flex items-center justify-center"
            >
              <X size={18} />
            </button>
            <div className="label-cap mb-2">
              {statuses[selectedWorker.id]?.is_clocked_in ? "Fichar salida" : "Fichar entrada"}
            </div>
            <h2 className="font-display text-3xl font-bold tracking-tight mb-1">{selectedWorker.name}</h2>
            <p className="text-sm text-[var(--text-secondary)] mb-6">Introduce tu contraseña</p>

            <div className="mb-6 relative">
              <input
                type={showPin ? "text" : "password"}
                autoFocus
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && submit()}
                onPaste={(e) => {
                  const text = e.clipboardData.getData("text").trim();
                  if (text) {
                    e.preventDefault();
                    setPin(text);
                  }
                }}
                data-testid="fichaje-pin-input"
                inputMode="text"
                autoComplete="off"
                spellCheck={false}
                placeholder="Contraseña o tarjeta RFID"
                className="w-full text-center py-4 pr-12 text-2xl font-mono tracking-widest border-b-2 bg-transparent outline-none placeholder:text-[var(--border)] placeholder:tracking-normal placeholder:text-lg"
                style={{ borderColor: "var(--primary)" }}
              />
              <button
                type="button"
                onClick={() => setShowPin((v) => !v)}
                data-testid="toggle-pin-visibility"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-lg hover:bg-[var(--bg)] text-[var(--text-secondary)]"
                aria-label={showPin ? "Ocultar" : "Mostrar"}
                tabIndex={-1}
              >
                {showPin ? <EyeSlash size={18} weight="duotone" /> : <Eye size={18} weight="duotone" />}
              </button>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-6">
              {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((k) => (
                <button
                  key={k}
                  data-testid={`keypad-${k}`}
                  onClick={() => pressKey(k)}
                  className="card-flat py-4 font-display text-2xl font-semibold hover:bg-[var(--bg)] active:scale-95 transition"
                >
                  {k}
                </button>
              ))}
              <button
                onClick={() => setPin("")}
                data-testid="keypad-clear"
                className="card-flat py-4 font-display text-sm font-semibold hover:bg-[var(--bg)] active:scale-95 transition text-[var(--text-secondary)]"
              >
                Limpiar
              </button>
              <button
                onClick={() => pressKey("0")}
                data-testid="keypad-0"
                className="card-flat py-4 font-display text-2xl font-semibold hover:bg-[var(--bg)] active:scale-95 transition"
              >
                0
              </button>
              <button
                onClick={backspace}
                data-testid="keypad-backspace"
                className="card-flat py-4 font-display text-sm font-semibold hover:bg-[var(--bg)] active:scale-95 transition text-[var(--text-secondary)] flex items-center justify-center"
              >
                <Backspace size={20} weight="duotone" />
              </button>
            </div>

            <button
              onClick={() => submit()}
              disabled={loading || !pin}
              data-testid={statuses[selectedWorker.id]?.is_clocked_in ? "fichar-salida-btn" : "fichar-entrada-btn"}
              className={`w-full py-4 rounded-xl font-display font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed ${
                statuses[selectedWorker.id]?.is_clocked_in ? "btn-danger" : "btn-primary"
              }`}
            >
              {loading ? "Procesando…" : statuses[selectedWorker.id]?.is_clocked_in ? "Confirmar salida" : "Confirmar entrada"}
            </button>

            <p className="text-xs text-center text-[var(--text-secondary)] mt-4">
              Puedes escribir con el teclado, <strong>pegar</strong> (Ctrl+V) o <strong>escanear una tarjeta RFID</strong>.
            </p>
          </div>
        </div>
      )}

      {/* Invoice upload PIN modal */}
      {uploadPinModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="card-flat w-full max-w-md p-8 relative">
            <button
              onClick={() => { setUploadPinModal(false); setUploadPin(""); }}
              className="absolute top-4 right-4 w-8 h-8 rounded-full hover:bg-[var(--border)] flex items-center justify-center"
            >
              <X size={18} />
            </button>
            <div className="label-cap mb-2">carga de factura</div>
            <h2 className="font-display text-2xl font-bold tracking-tight mb-2">Introduce el PIN</h2>
            <p className="text-sm text-[var(--text-secondary)] mb-6">
              Escribe el PIN de 4 dígitos y elige la foto de la factura.
            </p>
            <div className="mb-5">
              <PinPad
                value={uploadPin}
                onChange={(v) => setUploadPin(v.replace(/\D/g, "").slice(0, 4))}
                maxLength={4}
                testIdPrefix="upload-pinpad"
                inputTestId="upload-pin-input"
                placeholder="••••"
              />
            </div>
            <label className={`block w-full text-center py-4 rounded-xl btn-primary font-display font-bold cursor-pointer ${
              uploadingInvoice || uploadPin.length !== 4 ? "opacity-50 pointer-events-none" : ""
            }`} data-testid="upload-pin-file-label">
              {uploadingInvoice ? "Analizando…" : "Elegir foto"}
              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  submitInvoiceUpload(f);
                }}
                className="hidden"
                disabled={uploadingInvoice || uploadPin.length !== 4}
              />
            </label>
          </div>
        </div>
      )}

      {/* Invoice upload success */}
      {invoiceResult && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="card-flat w-full max-w-lg p-8">
            <div className="label-cap mb-2 text-[var(--primary)]">factura guardada</div>
            <h2 className="font-display text-2xl font-bold tracking-tight mb-1">
              {invoiceResult.supplier.name}
            </h2>
            <div className="text-sm text-[var(--text-secondary)] mb-4">
              Nº {invoiceResult.invoice.invoice_number || "—"} · {invoiceResult.invoice.invoice_date || "—"} · Total {invoiceResult.invoice.total ?? "—"} €
            </div>
            <div className="text-sm mb-4">
              {invoiceResult.invoice.lines.length} línea(s) extraídas.
              {invoiceResult.supplier_created && <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-[var(--primary)] text-white">Proveedor nuevo</span>}
            </div>
            {invoiceResult.price_alerts && invoiceResult.price_alerts.length > 0 && (
              <div className="p-3 rounded-lg bg-[#FDECEA] border border-[var(--danger)] mb-4">
                <div className="font-display font-bold text-[var(--danger)] text-sm">
                  ⚠ {invoiceResult.price_alerts.length} cambio(s) de precio
                </div>
                <div className="text-xs text-[var(--text-secondary)]">
                  Consulta el detalle desde el admin.
                </div>
              </div>
            )}
            <button
              onClick={() => setInvoiceResult(null)}
              className="w-full py-3 rounded-xl btn-primary font-display font-bold"
              data-testid="invoice-result-close"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}

      {incidenceWorker && (
        <IncidenceDialog
          worker={incidenceWorker}
          prefilledPin={incidencePin}
          onClose={() => { setIncidenceWorker(null); setIncidencePin(""); }}
        />
      )}

      {historyWorker && (
        <WorkerHistoryDialog
          worker={historyWorker}
          onClose={() => setHistoryWorker(null)}
          onOpenIncidence={(w) => {
            const pin = w.__pin || historyWorker.__pin || "";
            setHistoryWorker(null);
            setIncidencePin(pin);
            setIncidenceWorker(w);
          }}
        />
      )}

      {breakEndPrompt && (
        <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !loading && setBreakEndPrompt(null)}>
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={(e) => {
              e.preventDefault();
              submit({ break_end_time: breakEndPrompt.break_end_time });
            }}
            className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-2xl"
            data-testid="break-end-prompt"
          >
            <div className="mb-4">
              <div className="label-cap text-amber-800">tarea aún abierta</div>
              <h3 className="font-display font-bold text-lg">¿Cuándo terminó la tarea?</h3>
              <p className="text-xs text-[var(--text-secondary)] mt-1">
                Motivo: <strong>{breakEndPrompt.open_break?.reason}{breakEndPrompt.open_break?.custom_reason ? ` — ${breakEndPrompt.open_break.custom_reason}` : ""}</strong>
                {breakEndPrompt.open_break?.start_time && (
                  <> · Inicio: {new Date(breakEndPrompt.open_break.start_time).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}</>
                )}
              </p>
            </div>
            <input
              type="time"
              value={breakEndPrompt.break_end_time}
              onChange={(e) => setBreakEndPrompt((p) => ({ ...p, break_end_time: e.target.value }))}
              data-testid="break-end-time-input"
              className="w-full px-4 py-3 rounded-xl border border-[var(--border)] bg-white text-center font-mono text-2xl"
              required
            />
            <p className="text-xs text-amber-800 mt-2">
              La tarea se cerrará a esa hora y se creará un tramo de horas trabajadas desde entonces hasta la salida.
            </p>
            <div className="flex gap-2 mt-4">
              <button type="button" onClick={() => setBreakEndPrompt(null)} disabled={loading} className="flex-1 py-3 rounded-xl border border-[var(--border)] font-display font-semibold">
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading}
                data-testid="break-end-confirm"
                className="flex-1 py-3 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-display font-bold"
              >
                {loading ? "…" : "Confirmar y fichar salida"}
              </button>
            </div>
          </form>
        </div>
      )}

      {breakWorker && (
        <BreakDialog
          worker={breakWorker}
          prefilledPin={breakPin}
          onClose={(changed) => {
            setBreakWorker(null);
            setBreakPin("");
            if (changed) fetchWorkers();
          }}
        />
      )}

      {gateWorker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={closeGate}>
          <div className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-2xl max-h-[95vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-display font-bold text-xl">
                {gateAction === "history" ? "Ver mis horas" : "Nueva incidencia"}
              </h3>
              <button onClick={closeGate} className="p-1 rounded hover:bg-[var(--bg)]" data-testid="gate-close">
                <X size={18} />
              </button>
            </div>
            <div className="label-cap mb-4">{gateWorker.name}</div>
            <label className="label-cap block mb-2">Contraseña</label>
            <PinPad
              value={gatePin}
              onChange={setGatePin}
              onEnter={submitGate}
              maxLength={11}
              testIdPrefix="gate-pinpad"
              inputTestId="gate-pin-input"
              placeholder="••••"
            />
            {gateError && (
              <p className="text-[var(--danger)] text-sm mt-2" data-testid="gate-error">{gateError}</p>
            )}
            <button
              onClick={submitGate}
              disabled={gateChecking}
              data-testid="gate-submit"
              className="w-full mt-4 py-3 rounded-xl btn-primary font-display font-bold disabled:opacity-60"
            >
              {gateChecking ? "Verificando…" : "Continuar"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
